import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { githubDigestPath, rootPath, signInPath, whatIsAGithubDigestPath } from '@/routes'
import { Link } from '@inertiajs/react'
import { ArrowRight, HelpCircleIcon } from 'lucide-react'

const SIGN_IN_CAMPAIGN = 'utm_source=ai-founder-github-digest&utm_campaign=founder'

const linkClass = 'text-foreground decoration-golden-500 underline underline-offset-4'

type Faq = {
  question: string
  answer: string
}

function TextLink({ href, children }: { href: string; children: React.ReactNode }) {
  return (
    <Link href={href} className={linkClass}>
      {children}
    </Link>
  )
}

export function AiFounderGithubDigestArticle({ headline, faqs }: { headline: string; faqs: Faq[] }) {
  return (
    <div className="mx-auto max-w-4xl space-y-12 md:space-y-16">
      <header className="space-y-4 text-center">
        <p className="text-golden-500 text-sm font-semibold tracking-wider uppercase">For founders</p>
        <h1 className="text-foreground text-4xl font-bold tracking-tight text-balance sm:text-5xl md:text-6xl">
          {headline}
        </h1>
        <p className="text-muted-foreground mx-auto max-w-2xl text-lg leading-relaxed sm:text-xl">
          An AI founder digest GitHub search often means a technical founder who wants to see what shipped, without a
          live standup. <TextLink href={rootPath()}>Git Digest</TextLink> sends that founder GitHub digest to Slack and
          email, daily or weekly, from the repositories you pick.
        </p>
      </header>

      <Card className="border-golden-500/20 from-golden-500/5 via-background to-muted/30 bg-gradient-to-br p-6 text-center sm:p-8">
        <p className="text-muted-foreground text-sm leading-relaxed sm:text-base">
          The digest is built from GitHub, not from a form. Founders read what landed. The team does not retype a
          standup.
        </p>
      </Card>

      <section className="space-y-4">
        <h2 className="text-foreground text-3xl tracking-tight sm:text-4xl">Why founders want a GitHub digest</h2>
        <div className="text-muted-foreground space-y-4 text-base leading-relaxed">
          <p>
            A founder GitHub digest is for the person who owns the product and still wants to know what the code did.
            That includes founders of AI products who cannot sit in a status meeting every morning. Sign in with GitHub
            and choose the repositories that belong in the digest. Only those repos are included.
          </p>
          <p>
            Email goes to workspace members who have the email digest turned on, at the email hour you set. Slack goes
            to the channel stored when you connect Slack, at the Slack hour you set. Digests are prepared for
            workspaces with an active subscription. The homepage Unlimited plan is $29 per month, or $25 per month on
            yearly billing, for the team rather than per seat. A lifetime option is $1,000 once.
          </p>
        </div>
      </section>

      <section className="space-y-4">
        <h2 className="text-foreground text-3xl tracking-tight sm:text-4xl">Daily or weekly</h2>
        <div className="text-muted-foreground space-y-4 text-base leading-relaxed">
          <p>
            A daily digest covers the previous 24 hours. A weekly digest covers the past week and is prepared on
            Mondays. Daily fits a founder who checks Slack each morning. Weekly fits a founder who wants one read on
            Monday.
          </p>
          <p>
            Each change is a short summary, the author, and a link back to GitHub. When the change belongs to a pull
            request, the link uses that pull request number. Otherwise it uses a short commit id. Slack groups the
            lines under Feature, Bugfix, Improvement, Chore, and Refactoring. Email shows the first five changes in
            each repository, with lines added and removed. If the window is empty, Slack says there were no code
            changes.
          </p>
        </div>
      </section>

      <section className="space-y-4">
        <h2 className="text-foreground text-3xl tracking-tight sm:text-4xl">Standup bots and Gitingest</h2>
        <div className="text-muted-foreground space-y-4 text-base leading-relaxed">
          <p>
            A standup bot asks people what they did. This digest does not. It reports commits that already landed. When
            the work never becomes a commit, such as hiring or support, keep a short meeting. A form bot is a better
            fit for work that is not in GitHub.
          </p>
          <p>
            Some results for an AI founder digest GitHub query describe Gitingest, which turns a repository into text
            for a language model. Git Digest summarizes what landed and posts that to Slack and email. Read{' '}
            <TextLink href={whatIsAGithubDigestPath()}>what a GitHub digest is</TextLink>, then the{' '}
            <TextLink href={githubDigestPath()}>GitHub digest comparison</TextLink> with Gitingest.
          </p>
        </div>
      </section>

      <section className="space-y-6">
        <div>
          <h2 className="text-foreground text-3xl tracking-tight sm:text-4xl">Founder GitHub digest FAQ</h2>
          <p className="text-muted-foreground mt-3 text-base">
            Short answers for founders who want a digest of what shipped, not another standup.
          </p>
        </div>
        <div className="grid gap-4 md:grid-cols-2">
          {faqs.map((faq) => (
            <Card key={faq.question} className="h-full">
              <CardContent className="pt-6">
                <h3 className="text-foreground mb-3 flex items-start gap-x-3 text-lg">
                  <span className="flex h-[1lh] items-center">
                    <HelpCircleIcon className="text-golden-500 size-4 shrink-0" />
                  </span>
                  {faq.question}
                </h3>
                <p className="text-muted-foreground text-sm leading-relaxed">{faq.answer}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      <Card className="border-orange-200/50 bg-gradient-to-br from-orange-50 to-orange-100/30 p-8 text-center dark:border-orange-800/30 dark:from-orange-500/10 dark:to-orange-500/5">
        <h2 className="mb-3 text-2xl text-gray-900 dark:text-white">Send founders a GitHub digest</h2>
        <p className="text-muted-foreground mx-auto mb-6 max-w-2xl text-sm">
          Connect GitHub, pick the repositories, and read what shipped in Slack or email.
        </p>
        <Button
          asChild
          size="lg"
          className="bg-golden-500 hover:bg-golden-600 font-semibold text-black shadow-lg hover:shadow-xl"
        >
          <Link href={`${signInPath()}?${SIGN_IN_CAMPAIGN}`}>
            Start with GitHub
            <ArrowRight className="ml-2 size-4 shrink-0" />
          </Link>
        </Button>
      </Card>
    </div>
  )
}
